import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";
import { SetStateAction } from "jotai";
import { Dispatch, useState } from "react";

import { defaultFiltersParams } from "#app/routes/pages/Search.page";
import { useCompany } from "#modules/company/view/useCompany";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { PartWithChildrenDto } from "#modules/parts/infra/fetchPartWithChildren.api";
import { AssemblyTreeSection } from "#modules/similarPart/view/assembly/AssemblyTreeSection";
import { PreviewErrorCheck } from "#modules/similarPart/view/errors/PreviewErrorCheck";
import { ResultContent } from "#modules/similarPart/view/ResultContent";
import { ResultToolbar } from "#modules/similarPart/view/ResultToolbar";
import { Box } from "#shared/view/components/Box";
import { DEFAULT_DIMENSION_WEIGHT } from "#shared/view/components/FilterButton";
import { Flex } from "#shared/view/components/Flex";
import { Space } from "#shared/view/components/Space";
import { FilterQuery } from "shared";

import { useSearchTopology } from "../infra/useSearchTopology";

export const SimilarPartSearchResultsPage = ({
  part,
  assembly,
  dataChunkSize,
  setPart,
  isAssemblyAnalyzing,
}: {
  part: PartDto;
  assembly: PartWithChildrenDto;
  dataChunkSize: number;
  setPart: Dispatch<SetStateAction<PartDto | null>>;
  isAssemblyAnalyzing: boolean;
}) => {
  const [filtersParams, setFiltersParams] = useState<FilterQuery>(defaultFiltersParams);
  const { company } = useCompany();
  const [displayFeatures, { toggle: toggleDisplayFeatures }] = useDisclosure(false);
  const [displayScore, { toggle: toggleDisplayScore }] = useDisclosure(false);
  const [dimensionWeight, setDimensionWeight] = useState(DEFAULT_DIMENSION_WEIGHT);

  const {
    similarParts,
    refetch,
    isLoading: isSearching,
    isFetching,
  } = useSearchTopology(
    {
      id: part.id,
      filtersParams,
      limit: dataChunkSize,
      shouldReturnChildrenOfAssembly: company?.companySettings.shouldDisplayChildrenAsSearchResults,
      dimensionWeight,
    },
    { enabled: !!part.hasTopologicalEmbedding },
  );

  return (
    <PreviewErrorCheck part={part}>
      <Flex gap={"md"}>
        <AssemblyTreeSection
          assembly={assembly}
          part={part}
          setPart={setPart}
          isAssemblyAnalyzing={isAssemblyAnalyzing}
          refetch={refetch}
        />
        <Box w="100%">
          <ResultToolbar
            filtersParams={filtersParams}
            setFiltersParams={setFiltersParams}
            onSearch={refetch}
            part={part}
            title={t("similarPart.similar_parts")}
            toggleDisplayFeatures={toggleDisplayFeatures}
            displayFeatures={displayFeatures}
            toggleDisplayScore={toggleDisplayScore}
            displayScore={displayScore}
            dimensionWeight={dimensionWeight}
            setDimensionWeight={setDimensionWeight}
          />

          <Space h="xxs" />

          <ResultContent
            similarParts={similarParts}
            part={part}
            setPart={setPart}
            isSearching={isSearching}
            isFetching={isFetching}
            displayFeatures={displayFeatures}
            displayScore={displayScore}
          />
        </Box>
      </Flex>
    </PreviewErrorCheck>
  );
};
